import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// ContributoForfettario di pages/VerificaBandi.js (passo 4 della scheda di verifica): quello che React ricalcolava
// a ogni modifica della tabella. Sta sul contenitore del riquadro, che si vede solo con la casella «Contributo a somme
// forfettarie» (dati.contributo_forfettario && <Riquadro>).
// - Il riassunto del riquadro «N interventi · N soglie».
// - Le caselle degli interventi di ogni condizione tecnica: i codici della tabella ammessi dal tipo (o «tutti gli
//   interventi del progetto», o l'avviso), con il momento del tipo; cambiando il tipo gli interventi scelti si
//   azzerano (setC(k, { tipo, codici: [] })).
// - Il codice di una soglia ha due campi (uno nel blocco della quantità, uno in quello di non_solo / obbligatorio): si
//   tengono uguali e si invia quello visibile (data-gemello).
// - «Usa la proposta» scrive la categoria proposta da EROGO nell'intervento.
// - «Prova del calcolo»: visibile con almeno un intervento; un intervento con il codice per riga; le scelte
//   (prova.scelte: on e quantità, nell'ordine in cui si toccano, la quantità null finché non si scrive) restano anche
//   se il codice cambia o sparisce, come in React; «Calcola» le manda come dati[prova][scelte][n] con il form. Ogni
//   modifica della tabella o della prova azzera l'esito (setEsito(null)); aggiungere un intervento o una soglia no.
export default class extends Controller {
  static targets = ["interventi", "soglie", "tecniche", "prova", "scelte", "modelloScelta", "esito"]
  static values = { tecnici: Object }

  connect() {
    this.prova = new Map()
    this.aggiornaInterventi()
    this.gemelli()
  }

  // change@document: le due caselle dei modi di calcolo si escludono (dati_passo_controller#esclusivi), il riquadro
  // segue quella del contributo forfettario
  modo(event) {
    const form = this.element.closest("form")
    const casella = form?.querySelector("[data-dati-passo-target~='forfettario']")
    if (!casella || !form.contains(event.target)) return
    if (event.target.dataset.datiPassoTarget === undefined) return
    this.element.hidden = !casella.checked
  }

  cambio(event) {
    const el = event.target
    if (!this.element.contains(el)) return
    const riga = el.closest?.("[data-righe-target~='riga']")
    if (el.dataset?.gemello && riga) {
      riga.querySelectorAll(`[data-gemello='${el.dataset.gemello}']`).forEach((x) => { if (x !== el) x.value = el.value })
    }
    // solo codice, nome e «per unità» cambiano le caselle: il change di un altro campo all'uscita non le rifà (il
    // clic che lo provoca andrebbe perso su una casella appena sostituita)
    if (riga?.dataset.forfettarioIntervento !== undefined && /\[(codice|nome|per_unita)\]$/.test(el.name || "")) this.aggiornaInterventi()
    if (riga?.dataset.forfettarioCtec !== undefined && event.type === "change" && /\[tipo\]$/.test(el.name || "")) {
      riga.querySelectorAll("[data-elenco-ordinato='codici'] [data-valore]").forEach((x) => x.remove())
      this.codici(riga)
    }
    if (el.dataset?.forfettarioOn !== undefined || el.dataset?.forfettarioQuantita !== undefined) this.scegli(el)
    this.gemelli()
    this.azzeraEsito()
  }

  aggiunta(event) {
    const blocco = event.target
    if (blocco === this.tecnicheTarget) {
      const riga = event.detail?.riga
      if (riga) this.codici(riga)
      this.azzeraEsito()
    }
    if (blocco === this.interventiTarget) this.aggiornaInterventi()
    this.gemelli()
    this.riassunto()
  }

  // turbo:morph-element: dopo il salvataggio la scheda riletta riscrive le liste; le caselle delle condizioni tecniche,
  // le righe della prova, i codici delle soglie e il riassunto si riallineano (l'evento del contenitore arriva per ultimo)
  riletto(event) {
    if (event.target !== this.element) return
    this.aggiornaInterventi()
    this.gemelli()
    this.riassunto()
  }

  tolta() {
    this.gemelli()
    this.aggiornaInterventi()
    this.riassunto()
    this.azzeraEsito()
  }

  usaProposta(event) {
    const riga = event.currentTarget.closest("[data-righe-target~='riga']")
    const campo = riga?.querySelector("[name$='[categoria]']")
    if (!campo) return
    campo.value = event.params.proposta
    const menu = campo.closest("[data-controller~='select']")
    this.application.getControllerForElementAndIdentifier(menu, "select")?.sync()
    campo.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // «Calcola» (calcola() di React, un api.post per conto suo): i campi del form del passo (la tabella scritta nella
  // pagina) con la dimensione e le scelte accese nell'ordine in cui sono state toccate; la risposta è il turbo-stream
  // dell'esito; senza una risposta leggibile «Calcolo non riuscito».
  async calcola(event) {
    const pulsante = event.currentTarget
    const dati = new FormData(this.element.closest("form"))
    let n = 0
    this.scelteInOrdine().forEach(([codice, v]) => {
      if (!v.on) return
      dati.append(`dati[prova][scelte][${n}][codice]`, codice)
      if (v.quantita !== undefined) dati.append(`dati[prova][scelte][${n}][quantita]`, v.quantita)
      n += 1
    })
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const risposta = await fetch(pulsante.dataset.url, {
        method: "POST", body: dati,
        headers: { Accept: "text/vnd.turbo-stream.html", ...(token ? { "X-CSRF-Token": token } : {}) },
      })
      if (risposta.redirected || !(risposta.headers.get("Content-Type") || "").includes("turbo-stream")) throw new Error("risposta")
      Turbo.renderStreamMessage(await risposta.text())
    } catch {
      this.esitoTarget.replaceChildren()
      const p = document.createElement("p")
      p.className = "text-xs text-rose-700"
      p.dataset.testid = "prova-errore"
      p.textContent = "Calcolo non riuscito"
      this.esitoTarget.append(p)
    }
  }

  // Object.entries(prova.scelte): prima le chiavi che sono indici di array («0», «12»: in ordine numerico), poi le altre
  // nell'ordine in cui sono entrate
  scelteInOrdine() {
    const indice = (k) => /^(0|[1-9]\d*)$/.test(k) && Number(k) < 4294967295
    const voci = [...this.prova.entries()]
    return [...voci.filter(([k]) => indice(k)).sort(([a], [b]) => Number(a) - Number(b)), ...voci.filter(([k]) => !indice(k))]
  }

  azzeraEsito() {
    if (this.hasEsitoTarget) this.esitoTarget.innerHTML = ""
  }

  righeDi(blocco) {
    return [...blocco.querySelectorAll("[data-righe-target~='riga']")]
  }

  valore(riga, campo) {
    const casella = riga.querySelector(`input[type='checkbox'][name$='[${campo}]']`)
    if (casella) return casella.checked
    return riga.querySelector(`[name$='[${campo}]']`)?.value ?? ""
  }

  interventi() {
    return this.righeDi(this.interventiTarget).map((r) => ({
      codice: this.valore(r, "codice"), nome: this.valore(r, "nome"), perUnita: this.valore(r, "per_unita")
    }))
  }

  riassunto() {
    const testo = this.element.querySelector("[data-testid='riquadro-forfettario-riassunto']")
    if (testo) testo.textContent = `${this.righeDi(this.interventiTarget).length} interventi · ${this.righeDi(this.soglieTarget).length} soglie`
  }

  aggiornaInterventi() {
    if (this.hasTecnicheTarget) this.righeDi(this.tecnicheTarget).forEach((r) => this.codici(r))
    this.scriviProva()
  }

  // CondizioniTecniche: gli interventi di una condizione
  codici(riga) {
    const box = riga.querySelector("[data-forfettario-codici]")
    if (!box) return
    const def = this.tecniciValue
    const t = (def.tipi || {})[this.valore(riga, "tipo")] || {}
    const ammessiDalTipo = t.codici || []
    const scelti = [...riga.querySelectorAll("[data-elenco-ordinato='codici'] [data-valore]")].map((x) => x.value)
    const k = riga.dataset.righeIndice
    const span = (testo, cls) => { const s = document.createElement("span"); if (cls) s.className = cls; s.textContent = testo; return s }
    const nodi = [span("Interventi:", "text-zinc-500")]
    const ammessi = this.interventi().map((i) => i.codice).filter(Boolean).filter((c) => ammessiDalTipo.includes(c))
    const firma = JSON.stringify([k, ammessiDalTipo.includes(def.tutti), ammessi, t.fase, scelti])
    if (box.dataset.firma === firma) return
    box.dataset.firma = firma
    if (ammessiDalTipo.includes(def.tutti)) {
      nodi.push(span("tutti gli interventi del progetto"))
    } else if (ammessi.length) {
      ammessi.forEach((c) => {
        const label = document.createElement("label")
        label.className = "flex items-center gap-1.5 cursor-pointer"
        const input = document.createElement("input")
        input.type = "checkbox"
        input.value = c
        input.checked = scelti.includes(c)
        input.dataset.ordinato = "codici"
        input.dataset.testid = `ctec-${k}-codice-${c}`
        label.append(input, ` ${c}`)
        nodi.push(label)
      })
    } else {
      nodi.push(span("nessun intervento della tabella a cui si applica", "text-amber-700"))
    }
    nodi.push(span(`· ${(def.fasi || {})[t.fase] ?? ""}`, "text-zinc-400"))
    box.replaceChildren(...nodi)
  }

  // il codice visibile di una soglia si invia (e solo lui ha il testid); se nessuno si vede, il primo
  gemelli() {
    if (!this.hasSoglieTarget) return
    this.righeDi(this.soglieTarget).forEach((riga) => {
      const gemelli = [...riga.querySelectorAll("[data-gemello='codice']")]
      const nascosto = (el) => { for (let n = el.parentElement; n && n !== riga; n = n.parentElement) if (n.hidden) return true; return false }
      const visibili = gemelli.filter((el) => !nascosto(el))
      const attivo = visibili[0] || gemelli[0]
      // un solo elemento ha il testid soglia-{k}-codice: quello che si invia
      gemelli.forEach((el) => {
        el.disabled = el !== attivo
        if (el === attivo) el.dataset.testid = `soglia-${riga.dataset.righeIndice}-codice`
        else delete el.dataset.testid
      })
    })
  }

  // «Prova del calcolo»: una riga per ogni intervento con il codice
  scriviProva() {
    if (!this.hasProvaTarget) return
    const interventi = this.interventi()
    this.provaTarget.hidden = interventi.length === 0
    const elenco = interventi.filter((i) => i.codice)
    const presenti = [...this.scelteTarget.querySelectorAll("[data-forfettario-scelta]")]
    // gli stessi codici: si aggiornano etichetta e «per unità» al loro posto
    if (presenti.length === elenco.length && presenti.every((n, j) => n.dataset.codice === elenco[j].codice)) {
      presenti.forEach((n, j) => {
        const i = elenco[j]
        n.dataset.perUnita = String(i.perUnita)
        n.querySelector("[data-forfettario-etichetta]").textContent = `${i.codice} ${i.nome}`
        n.querySelector("[data-forfettario-unita]").hidden = !(i.perUnita && this.prova.get(i.codice)?.on)
      })
      return
    }
    const righe = elenco.map((i) => {
      const v = this.prova.get(i.codice) || {}
      const nodo = this.modelloSceltaTarget.content.firstElementChild.cloneNode(true)
      nodo.dataset.codice = i.codice
      nodo.dataset.perUnita = String(i.perUnita)
      const on = nodo.querySelector("[data-forfettario-on]")
      on.checked = !!v.on
      on.dataset.testid = `prova-${i.codice}`
      nodo.querySelector("[data-forfettario-etichetta]").textContent = `${i.codice} ${i.nome}`
      const q = nodo.querySelector("[data-forfettario-quantita]")
      q.value = v.quantita ?? 1
      q.dataset.testid = `prova-${i.codice}-quantita`
      nodo.querySelector("[data-testid$='-limite']").dataset.testid = `prova-${i.codice}-limite`
      nodo.querySelector("[data-forfettario-unita]").hidden = !(i.perUnita && v.on)
      return nodo
    })
    this.scelteTarget.replaceChildren(...righe)
  }

  // setV: la scelta di un codice (la prima volta entra in fondo a prova.scelte)
  scegli(el) {
    const nodo = el.closest("[data-forfettario-scelta]")
    const codice = nodo?.dataset.codice
    if (codice === undefined) return
    const v = { ...(this.prova.get(codice) || {}) }
    if (el.dataset.forfettarioOn !== undefined) v.on = el.checked
    else v.quantita = el.value
    this.prova.set(codice, v)
    this.scelteTarget.querySelectorAll("[data-forfettario-scelta]").forEach((n) => {
      if (n.dataset.codice !== codice) return
      n.querySelector("[data-forfettario-on]").checked = !!v.on
      n.querySelector("[data-forfettario-unita]").hidden = !(n.dataset.perUnita === "true" && v.on)
      const q = n.querySelector("[data-forfettario-quantita]")
      if (q !== el) q.value = v.quantita ?? 1
    })
  }
}
